import { Injectable } from '@angular/core';
import {
  ApiResponsePaged,
  EdpdRequestsFormService,
  EdpdRequestsListService,
  FileUploaded,
  IRequestSubmittedSummary,
  projectCode,
} from '@eredes/dfcore';
import {
  AddAttachmentRequest,
  ContactUpdateRequest,
  GetReplyRequest,
  IRepliesResponse,
  MsCustomerService,
  UpdateCountersRequest,
  IAddAttachmentResponse,
  IContactUpdateResponse,
} from '@eredes/microservices';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AttachmentSubmit, FileToUpload, IAttachment } from '../_models/attachment.interface';
import {
  FormConfigurationFieldsCancelSubmit,
  FormConfigurationFieldsInsistenceSubmit,
  FormConfigurationFieldsReopenSubmit,
  FormConfigurationFieldsSubmit,
  FormConfigurationSubmit,
} from '../_models/request-form.interface';
import { RequestStatus } from '../_models/request-status.enum';

import { UserModel } from 'app/_core/models/user.model';

@Injectable()
export class RequestFormService {
  UUID_CONFIGS: any;

  constructor(
    private readonly edpdRequestsFormService: EdpdRequestsFormService,
    private readonly msCustomerService: MsCustomerService,
    private readonly requestListingService: EdpdRequestsListService
  ) {
    this.setUuidConfigs();
  }

  /**
   * Map and send the request for clarification or operation
   * @param fields fields with the values to be submitted
   * @param formConfiguration form base configuration
   * @param attachments files to be submitted
   */
  submit(
    fields: FormConfigurationFieldsSubmit,
    formConfiguration: FormConfigurationSubmit,
    attachments?: IAttachment[]
  ): Observable<any> {
    return this.uploadFormAttachments(attachments).pipe(
      switchMap((responseUpload: AttachmentSubmit[]) => {
        fields.attachments = responseUpload;
        const data = this.mapperForm(fields, formConfiguration.fields);

        return this.edpdRequestsFormService.submit(formConfiguration.formUuid, null, data, false);
      })
    );
  }

  /**
   * Update request by state
   * @param fields fields with the values to be updated
   * @param state current request state
   * @returns
   */
  updateRequest(fields: FormConfigurationFieldsInsistenceSubmit, state: string): Observable<any> {
    if (state === RequestStatus.PENDING_RETAILER) {
      return this.update(fields);
    }

    if (state === RequestStatus.IN_PROGRESS) {
      return this.updateCounters(fields);
    }

    return of({});
  }

  /**
   * Get logged current user
   */
  get user(): UserModel {
    const userJson = sessionStorage.getItem('currentUser');
    if (userJson) {
      return JSON.parse(userJson) as UserModel;
    }

    return null;
  }

  /**
   * Check if logged user can create request
   * @returns true | false
   */
  canCreate(): boolean {
    const SUFFIX_ALLOWS_SUBMISSION = 'SUBD';

    if (this.user && this.user.userClaims) {
      const { userClaims } = this.user;
      const validClaim = userClaims.find((item) => item.endsWith(SUFFIX_ALLOWS_SUBMISSION));

      return !!validClaim;
    }

    return false;
  }

  /**
   * Map and send the data to reopen request
   * @param formUuid request id to reopen
   * @param fields fields with the values to be submitted
   * @returns
   */
  reopen(formUuid: string, fields: FormConfigurationFieldsReopenSubmit): Observable<any> {
    const REOPEN_UUID = '6cc83f9a-c774-4199-99a3-ec55563b97ad';

    const FORM_CONFIG_UUIDS: FormConfigurationFieldsReopenSubmit = {
      contactId: '66a19f97-878f-4ea5-9382-eedcb0fa3001',
      reason: 'ca440e47-90e2-4967-95a1-b73200f28adc',
      description: '04e9e639-3e59-4d5b-b843-30f7d4faaf4b',
      transactionId: 'a8586e31-c5ad-4c9e-8a25-5e80ca65d3e8',
    };

    const data = this.mapperForm(fields, FORM_CONFIG_UUIDS);

    return this.edpdRequestsFormService.submit(REOPEN_UUID, formUuid, data, false);
  }

  /**
   * Map and send the data to cancel request
   * @param formUuid request id to cancel
   * @param fields fields with the values to be submitted
   * @param attachments files to be submitted
   * @returns
   */
  cancel(formUuid: string, fields: FormConfigurationFieldsCancelSubmit, attachments?: IAttachment[]): Observable<any> {
    const CANCEL_UUID = 'cb5a5db7-4a83-4051-b2d4-7287c98d9055';

    const FORM_CONFIG_UUIDS: FormConfigurationFieldsCancelSubmit = {
      contactId: '9ffce9a3-feb6-4704-97b6-a5ab747c572a',
      partnerNumber: '82c21da1-30fe-4c98-88a3-138e145bc399',
      attachments: '06f6b57c-f0db-456c-b02d-4080ad514971',
      description: '00958f8f-efc5-42a1-80a0-d1aee64b8697',
      transactionId: '493f82c8-edf3-4e54-b419-3d03c6c7312d',
    };

    return this.uploadFormAttachments(attachments).pipe(
      switchMap((responseUpload: AttachmentSubmit[]) => {
        fields.attachments = responseUpload;
        const data = this.mapperForm(fields, FORM_CONFIG_UUIDS);

        return this.edpdRequestsFormService.submit(CANCEL_UUID, formUuid, data, false);
      })
    );
  }

  /**
   * Get replies from a JUMP contact by contact id
   * @param contactId - contact id
   */
  getRepliesByContactId(contactId: string): Observable<IRepliesResponse> {
    const request: GetReplyRequest = {
      contact_id: contactId,
      emptyComments: false,
    };

    return this.msCustomerService.getReplies(request);
  }

  /**
   * Defines identifier settings used in order search filters
   */
  setUuidConfigs() {
    const STATES = {
      in_progress: 'fedcc139-1d07-485a-8cec-ca5243ab39f4',
      pending_retailer: '33e94523-30f2-4c23-be3c-fedd98145ccb',
      solved: '2cbbe70a-8875-11ea-bc55-0242ac130003',
      canceled: '2cbbdb5c-8875-11ea-bc55-0242ac130003',
    };

    const CATEGORIES = {
      clarification: {
        name: 'Pedido de esclarecimento',
        values: [
          '7fb1a13d-069d-4edc-9f47-2474f174b623',
          'b29549fb-de04-4909-a7b6-76786edcae88',
          '7b1cb5e1-637a-4eab-a380-4185dbf625c5',
          'ee546fb2-789e-4c3c-9201-8fb4201e876a',
          'e23381bd-bec6-44ca-9fd8-ff8b924e0176',
          '7db0d5f1-8604-43f9-bff9-072936d88f3a',
          '1aac7854-73a1-4a1c-8ee3-42551abfb5e7',
          'b83d5dc8-b770-493c-9bae-02c0203eb275',
          '58288839-8f62-4614-85aa-2d952808b493',
          '16e86d9d-6bf8-44b1-bcd6-b465609a1383',
          'b8f6a33e-ee33-4947-b2ae-e430e7767cdf',
          'c0abfa61-a304-4f44-800b-2ff52fe753de',
          '279cb347-6392-4f1c-ab8a-efa27f94561b',
          'd4d818e4-cb21-48cc-9dd9-8d7d2dfc46be',
          '913210be-e798-4696-9ccc-13c325b8e430',
          '1759d57e-5618-488d-a295-e7ece58b67df',
          'f6bf437d-bde4-4235-877b-a089692bdd2b',
          'b06b5025-a4a1-4b05-9e07-659b1f7fbb76',
          '609821e4-866c-4951-8242-fc8b92d00447',
          'dcffdb29-2fda-492d-806e-7845908bd692',
          '15e760d6-6595-4ac8-823a-c5102732f54f',
          '43deb833-e3f6-4c84-909e-3c3c15200f33',
          'b47e3146-3ef2-4baa-8e88-72d55f8cdfc8',
          'aceb8701-1e1e-4fdc-8660-ca8abdf1afcf',
          '01b49f15-d524-492d-9e53-003a6e3188d9',
          '68577eaf-f97e-411c-ac16-c299a1ad98f4',
          '381bc6e3-cb8a-48e1-bd48-2ad28d40d393',
          'c6c15bd3-b0cf-4cc3-bf21-9a6540af9cb5',
          'a9031e9c-4d98-403b-bcc3-b6726be2bd8c',
          '7457740c-1158-4ca4-be1f-90e7baa750d4',
          '0bd1d651-6c25-4461-9fab-91ace94bbc0b',
          'b0ab2a1a-e790-40e8-91c9-0eb7b3f9212c',
          'd57f3d20-7daa-4598-addb-05b0298db3cb',
          'fac436b9-cf93-42cf-8b76-8aa2d70ee3be',
          '381bc6e3-cb8a-48e1-bd48-2ad28d40d393',
          'c6c15bd3-b0cf-4cc3-bf21-9a6540af9cb5',
          'a9031e9c-4d98-403b-bcc3-b6726be2bd8c',
          'ebe037ae-854d-4196-b9d3-f0c942bb560b',
          'b80b0a2c-4778-47bd-a770-454454b8cb39',
          'b077a79f-419d-4e1d-ba17-a26c8d3378bb',
          '3d3ee674-fd4c-41c0-aea2-17f3dc91a381',
          '3a1b454d-c8f7-47e8-8e00-19bc72cf7667',
          '884de3f9-64c0-41aa-9b85-f9b9e2fa431a',
        ],
      },
      operation: {
        name: 'Pedido de operação',
        values: [
          'ae686586-bca4-43b2-b661-c9d3f81c9727',
          '6c0e0425-c650-4d9f-8b3c-053e3da12a7e',
          '8d76ab5e-a7d6-4d80-b4a7-b71dd8fda54d',
          'f92d7395-13db-4d6c-a633-70f428fc8b81',
          '854233e4-bc84-45b0-a4b2-69095347da81',
          '448b39d0-cac9-4559-9828-c8ac149f8ab1',
          '73599227-aa26-4177-bc00-519e0d693094',
          'b2a7722b-5d3f-4913-8158-68e8c8e45d74',
          '332224c3-c1cf-4438-8f27-1e5540b5b308',
          '3ae005d1-1d01-4e5e-842a-b17a30f33d2a',
          '49d3b444-ea45-485f-869b-8806327045bc',
          '781bfa8a-82db-4020-9e0d-7124bdc6a942',
          '998eab61-6466-40d2-9094-c1bc3ea69ac0',
          '2fd78a1f-1872-414c-8841-aaf3e7b265f8',
          'bc3f36ad-0be9-4140-859a-8268ac1cad69',
          '0ee3d421-edcc-4522-a44c-024c0c08ae7d',
          'b059b568-edfe-455a-a22f-76e75dc9fbac',
          'c1bd45ac-376e-4e2e-95e3-4073b043d22f',
          'c3e09c7a-da6c-4dd7-a8eb-5304713703dc',
          'fbd136bb-f46f-44be-8053-a580d3aa8900',
          '27994643-7a96-4330-a19e-204f1360eb07',
          'b554ed40-ec38-4fb9-8312-4f4e835a0725',
          '3cd07e95-0e30-46d9-93db-b1a7a4cc1c13',
          '46748401-b501-4cef-801d-5b7331c2634d',
          'd22c544d-f830-49ad-aa64-0b8f131540bf',
          '15bb8971-df25-4e6d-a76d-5ec194facefd',
          '9dbd1840-9db8-4d59-b28c-f0189fbcaddb',
          'a5fa36a1-d30f-4797-831f-81e9eb5caa28',
          'fbd136bb-f46f-44be-8053-a580d3aa8900',
        ],
      },
    };

    const AREAS = {
      network_access: {
        name: 'Acesso à Rede',
        values: [
          '16e86d9d-6bf8-44b1-bcd6-b465609a1383',
          'b8f6a33e-ee33-4947-b2ae-e430e7767cdf',
          'c0abfa61-a304-4f44-800b-2ff52fe753de',
          '279cb347-6392-4f1c-ab8a-efa27f94561b',
          'd4d818e4-cb21-48cc-9dd9-8d7d2dfc46be',
          '913210be-e798-4696-9ccc-13c325b8e430',
          '1759d57e-5618-488d-a295-e7ece58b67df',
          'f6bf437d-bde4-4235-877b-a089692bdd2b',
          'b06b5025-a4a1-4b05-9e07-659b1f7fbb76',
          '998eab61-6466-40d2-9094-c1bc3ea69ac0',
          '2fd78a1f-1872-414c-8841-aaf3e7b265f8',
          'bc3f36ad-0be9-4140-859a-8268ac1cad69',
          '0ee3d421-edcc-4522-a44c-024c0c08ae7d',
          'b059b568-edfe-455a-a22f-76e75dc9fbac',
        ],
      },
      collection_and_debt: {
        name: 'Cobrança e Dívida',
        values: [
          '8d76ab5e-a7d6-4d80-b4a7-b71dd8fda54d',
          'f92d7395-13db-4d6c-a633-70f428fc8b81',
          '854233e4-bc84-45b0-a4b2-69095347da81',
          '448b39d0-cac9-4559-9828-c8ac149f8ab1',
          '73599227-aa26-4177-bc00-519e0d693094',
          'b2a7722b-5d3f-4913-8158-68e8c8e45d74',
          '332224c3-c1cf-4438-8f27-1e5540b5b308',
          '3ae005d1-1d01-4e5e-842a-b17a30f33d2a',
          '49d3b444-ea45-485f-869b-8806327045bc',
          '7db0d5f1-8604-43f9-bff9-072936d88f3a',
          '781bfa8a-82db-4020-9e0d-7124bdc6a942',
          '1aac7854-73a1-4a1c-8ee3-42551abfb5e7',
          'b83d5dc8-b770-493c-9bae-02c0203eb275',
          '58288839-8f62-4614-85aa-2d952808b493',
        ],
      },
      energy_data: {
        name: 'Dados de Energia',
        values: [
          '43deb833-e3f6-4c84-909e-3c3c15200f33',
          'b47e3146-3ef2-4baa-8e88-72d55f8cdfc8',
          'aceb8701-1e1e-4fdc-8660-ca8abdf1afcf',
          '01b49f15-d524-492d-9e53-003a6e3188d9',
          '68577eaf-f97e-411c-ac16-c299a1ad98f4',
          '381bc6e3-cb8a-48e1-bd48-2ad28d40d393',
          'c6c15bd3-b0cf-4cc3-bf21-9a6540af9cb5',
          'a9031e9c-4d98-403b-bcc3-b6726be2bd8c',
          'c1bd45ac-376e-4e2e-95e3-4073b043d22f',
          'c3e09c7a-da6c-4dd7-a8eb-5304713703dc',
          'fbd136bb-f46f-44be-8053-a580d3aa8900',
          'ebe037ae-854d-4196-b9d3-f0c942bb560b',
          'b80b0a2c-4778-47bd-a770-454454b8cb39',
          'b077a79f-419d-4e1d-ba17-a26c8d3378bb',
          '3d3ee674-fd4c-41c0-aea2-17f3dc91a381',
          '3a1b454d-c8f7-47e8-8e00-19bc72cf7667',
        ],
      },
      billing: {
        name: 'Faturação',
        values: [
          '7fb1a13d-069d-4edc-9f47-2474f174b623',
          'b29549fb-de04-4909-a7b6-76786edcae88',
          '7b1cb5e1-637a-4eab-a380-4185dbf625c5',
          'ee546fb2-789e-4c3c-9201-8fb4201e876a',
          'e23381bd-bec6-44ca-9fd8-ff8b924e0176',
          'ae686586-bca4-43b2-b661-c9d3f81c9727',
          '6c0e0425-c650-4d9f-8b3c-053e3da12a7e',
          '884de3f9-64c0-41aa-9b85-f9b9e2fa431a',
        ],
      },
      fraud: {
        name: 'Fraude',
        values: ['7457740c-1158-4ca4-be1f-90e7baa750d4'],
      },
      addresses: {
        name: 'Moradas',
        values: ['27994643-7a96-4330-a19e-204f1360eb07'],
      },
      technical_questions: {
        name: 'Questões Técnicas',
        values: [
          '609821e4-866c-4951-8242-fc8b92d00447',
          'dcffdb29-2fda-492d-806e-7845908bd692',
          '15e760d6-6595-4ac8-823a-c5102732f54f',
        ],
      },
      transversal: {
        name: 'Transversal',
        values: [
          '0bd1d651-6c25-4461-9fab-91ace94bbc0b',
          'b0ab2a1a-e790-40e8-91c9-0eb7b3f9212c',
          'd57f3d20-7daa-4598-addb-05b0298db3cb',
          'fac436b9-cf93-42cf-8b76-8aa2d70ee3be',
          'b554ed40-ec38-4fb9-8312-4f4e835a0725',
          '3cd07e95-0e30-46d9-93db-b1a7a4cc1c13',
          '46748401-b501-4cef-801d-5b7331c2634d',
          'd22c544d-f830-49ad-aa64-0b8f131540bf',
          '15bb8971-df25-4e6d-a76d-5ec194facefd',
          '9dbd1840-9db8-4d59-b28c-f0189fbcaddb',
          'a5fa36a1-d30f-4797-831f-81e9eb5caa28',
        ],
      },
    };

    this.UUID_CONFIGS = { STATES, CATEGORIES, AREAS };
  }

  /**
   * List request
   * @param args object containing filters to apply, as well as pagination
   * @returns order search result
   */
  getRequests(args): Observable<ApiResponsePaged<IRequestSubmittedSummary>> {
    const filters = this.mapperToGetRequest(args);

    return this.requestListingService.getRequestList(filters, 'comercializadores');
  }

  /**
   * Export requests
   * @param args object containing filters to apply
   * @returns object containing csv file
   */
  exportRequests(args): Observable<any> {
    const filters = {
      type: 'csv',
      filters: this.mapperToGetRequest(args),
    };

    return this.requestListingService.export(filters, 'comercializadores');
  }

  /**
   * Returns options for order category types
   * @returns list of categories
   */
  getCategories(): any[] {
    const categories = [];
    const { CATEGORIES } = this.UUID_CONFIGS;
    const categoryKeys = Object.keys(CATEGORIES);
    categoryKeys.forEach((key) => {
      const { name } = CATEGORIES[key];
      categories.push({
        value: key,
        label: name,
      });
    });

    return categories;
  }

  /**
   * Returns area options by category
   * @param category to be filtered
   * @returns list of areas
   */
  getAreasByCategory(category): any[] {
    const { CATEGORIES, AREAS } = this.UUID_CONFIGS;
    const categoryValues = CATEGORIES[category].values;
    const areas = [];
    const areaKeys = Object.keys(AREAS);
    areaKeys.forEach((key) => {
      const { name, values } = AREAS[key];
      const intersection = values.filter((x) => categoryValues.includes(x));

      if (intersection && intersection.length > 0) {
        areas.push({
          value: key,
          label: name,
        });
      }
    });

    return areas;
  }

  /**
   * Convert user-entered information to the format the API expects in order search
   * @param args object containing the values referring to the search fields
   * @returns object with the values expected by the API
   */
  private mapperToGetRequest(args) {
    const { page, pageNum, search, date, states, category, areas } = args;

    let [dateFrom, dateTo] = date;
    dateFrom = dateFrom ? moment(dateFrom).format('YYYY-MM-DD') : null;
    dateTo = dateTo ? moment(dateTo).format('YYYY-MM-DD') : null;

    const { STATES, CATEGORIES, AREAS } = this.UUID_CONFIGS;
    const statusesUuids = states ? states.map((state) => STATES[state]) : [];
    const categoriesUuids = CATEGORIES[category] ? CATEGORIES[category].values : [];
    let areaUuids = [];
    if (areas) {
      areas.forEach((area) => {
        areaUuids = [...areaUuids, ...AREAS[area].values];
      });
    }

    let stepsUuids;
    if (categoriesUuids.length && areaUuids.length) {
      stepsUuids = categoriesUuids.filter((x) => areaUuids.includes(x));
    } else {
      if (categoriesUuids.length) {
        stepsUuids = categoriesUuids;
      } else if (areaUuids.length) {
        stepsUuids = areaUuids;
      }
    }

    return {
      page,
      pageNum,
      dateFrom: dateFrom ? dateFrom : null,
      dateTo: dateTo ? dateTo : null,
      search_value: search ? search : '',
      statusesUuids,
      stepsUuids,
    };
  }

  /**
   * Map and send the data to update counters
   * @param fields fields with the values to be updated
   */
  private updateCounters(fields: FormConfigurationFieldsInsistenceSubmit): Observable<any> {
    const DEFAULT_DESCRIPTION = 'Nova documentação colocada pelo comercializador';
    const {
      contactId,
      attachments,
      description,
      type,
      commChannel,
      destiny,
      interlocutor,
      attendingPoint,
      date,
      registryDate,
      registryTime,
      transactionId,
    } = fields;
    const text = description || DEFAULT_DESCRIPTION;

    const data: UpdateCountersRequest = {
      contact: contactId,
      text,
      reason: '',
      type,
      commChannel,
      destiny,
      interlocutor,
      attendingPoint,
      date,
      registryDate,
      registryTime,
      TransactionId: transactionId,
    };

    return this.msCustomerService.updateCounters(data).pipe(
      switchMap((updateResponse: IContactUpdateResponse) => {
        if (attachments?.length) {
          const { ReplyUuid } = updateResponse;

          return this.addAttachments(contactId, ReplyUuid, attachments);
        }

        return of(updateResponse);
      })
    );
  }

  /**
   * Map and send the data to update request
   * @param fields fields with the values to be updated
   */
  private update(fields: FormConfigurationFieldsInsistenceSubmit): Observable<any> {
    const CONTACT_TYPE = '10';
    const IN_INFORMATION_COLLECTION = '02';

    const { contactId, attachments, description, customerId, transactionId } = fields;
    const data: ContactUpdateRequest = {
      customerId,
      contactId,
      type: CONTACT_TYPE,
      subject: '',
      status: IN_INFORMATION_COLLECTION,
      comment: description,
      TransactionId: transactionId,
    };

    return this.msCustomerService.update(data).pipe(
      switchMap((updateResponse: IContactUpdateResponse) => {
        if (attachments?.length) {
          const { ReplyUuid } = updateResponse;

          return this.addAttachments(contactId, ReplyUuid, attachments);
        }

        return of(updateResponse);
      })
    );
  }

  /**
   * Send form attachments
   * @param attachments files to be submitted
   * @returns Observable of type {@link AttachmentSubmit} list
   */
  private uploadFormAttachments(attachments: IAttachment[]): Observable<AttachmentSubmit[]> {
    const uploadTask: Observable<FileUploaded>[] = [];
    const filesToUpload: FileToUpload[] = [];

    attachments.forEach((item: IAttachment) => {
      const fileToUpload: FileToUpload = {
        project: projectCode.COMERCIALIZADORES,
        user_uuid: '', // sent by proxy
        type: 'attachment',
        name: item.name,
        mime_type: item.type,
        size: item.size,
        content: (item.base64 as string).split('base64,')[1],
      };

      uploadTask.push(this.edpdRequestsFormService.attachmentUpload(fileToUpload));
      filesToUpload.push(fileToUpload);
    });

    if (uploadTask.length === 0) {
      return of([]);
    }

    return forkJoin(uploadTask).pipe(
      map((filesUploaded: any[]) => {
        return filesUploaded;
      })
    );
  }

  /**
   * Map the reported values according to the form type
   * @param formValues fields with the values to be submitted
   * @param formUuids form base configuration with uuids
   * @returns
   */
  private mapperForm(formValues: Record<string, any>, formUuids: Record<string, any>): any {
    const listKeys = Object.keys(formUuids);
    const object = {};

    listKeys.forEach((item) => {
      if (formUuids[item]) {
        const key = formUuids[item];
        const value = formValues[item];

        object[key] = value;
      }
    });

    return object;
  }

  /**
   * Send attachments linked to an request
   * @param contactId request identifier
   * @param replyUuid
   * @param attachments
   * @returns
   */
  private addAttachments(
    contactId: string,
    replyUuid: string,
    attachments: IAttachment[]
  ): Observable<IAddAttachmentResponse[]> {
    const attachmentsRequest: AddAttachmentRequest = this.mapperToAddAttachmentRequest(
      contactId,
      replyUuid,
      attachments
    );

    return this.msCustomerService.addAttachment(attachmentsRequest);
  }

  /**
   * Map list of {@link IAttachment} to {@link AddAttachmentRequest}
   * @param contactId request identifier
   * @param replyUuid
   * @param attachments
   * @returns
   */
  private mapperToAddAttachmentRequest(
    contactId: string,
    replyUuid: string = null,
    attachments: IAttachment[]
  ): AddAttachmentRequest {
    return {
      files: attachments.map((item: IAttachment) => {
        return {
          ContactID: contactId,
          DocData: item.base64.toString(),
          FileName: item.name,
          ReplyUuid: replyUuid,
          Size: item.size,
          TransactionId: item.transactionId,
        };
      }),
    };
  }
}
